import { useEffect, useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import {
  useDismissDispatcherForwardMutation,
  useListDispatcherForwardsQuery,
  type DispatcherForward,
} from '@shared/api';
import { useAgentPermission } from '@entities/user';
import { Skeleton } from '@shared/ui/Skeleton';
import { ToneBadge, formatDt, type Tone } from './tone';

/** `sent` — «Отправленные»; `review` — «Ждут решения»: ОСНОВА не выбрала
 *  адресата или отправка не прошла, решает человек. */
export type ForwardListMode = 'sent' | 'review';

interface SentTabProps {
  /** Открыть карточку письма во вкладке «Входящие» (по id переписки). */
  onOpen: (correspondenceId: string) => void;
  mode?: ForwardListMode;
}

// Фильтр по статусу пересылки. Пустая строка в «Отправленных» — sent | manual |
// corrected; `review` — pending_review | failed (см. backend
// forward_list_statuses).
type ForwardStatusFilter =
  | ''
  | 'review'
  | 'sent'
  | 'manual'
  | 'corrected'
  | 'not_needed'
  | 'pending_review'
  | 'failed';

const STATUS_META: Record<string, { label: string; tone: Tone }> = {
  sent:           { label: 'Отправлено',          tone: 'success' },
  manual:         { label: 'Отправлено вручную',   tone: 'success' },
  corrected:      { label: 'Исправлено юристом',   tone: 'info' },
  not_needed:     { label: 'Пересылка не нужна',   tone: 'neutral' },
  pending_review: { label: 'Адресат не выбран',    tone: 'warning' },
  failed:         { label: 'Ошибка отправки',      tone: 'danger' },
};

const FILTERS: Record<ForwardListMode, ReadonlyArray<[ForwardStatusFilter, string]>> = {
  sent: [
    ['', 'Все'],
    ['sent', 'Автоматически'],
    ['manual', 'Вручную'],
    ['corrected', 'Исправленные'],
    ['not_needed', 'Без пересылки'],
  ],
  review: [
    ['review', 'Все'],
    ['pending_review', 'Адресат не выбран'],
    ['failed', 'Ошибка отправки'],
  ],
};

/** Почему письмо не переслано — из разбора ОСНОВЫ или ошибки SMTP. */
function reviewReason(forward: DispatcherForward): string | null {
  if (forward.status === 'failed' && forward.last_error) {
    return `Не отправилось: ${forward.last_error}`;
  }
  if (forward.llm_reasoning?.dismissed_by) {
    const c = forward.llm_reasoning.dismiss_comment?.trim();
    return c ? `Отмечено «Не пересылать»: ${c}` : 'Отмечено «Не пересылать».';
  }
  const r = forward.llm_reasoning?.reasoning?.trim();
  return r ? r : null;
}

export function SentTab({ onOpen, mode = 'sent' }: SentTabProps) {
  const { can_write } = useAgentPermission('dispatcher');
  const [dismiss] = useDismissDispatcherForwardMutation();
  const [dismissing, setDismissing] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<ForwardStatusFilter>(
    mode === 'review' ? 'review' : '',
  );

  // Смена вкладки — фильтр по умолчанию этой вкладки.
  useEffect(() => {
    setStatusFilter(mode === 'review' ? 'review' : '');
    setActionError(null);
  }, [mode]);

  // Debounce поиска — 280мс, минимум 2 символа (как во «Входящих»):
  // не дёргаем backend на каждый символ.
  useEffect(() => {
    const trimmed = searchInput.trim();
    if (trimmed.length > 0 && trimmed.length < 2) {
      setSearchQuery('');
      return;
    }
    const t = window.setTimeout(() => setSearchQuery(trimmed), 280);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const { data, isLoading, isError, refetch } = useListDispatcherForwardsQuery({
    status: statusFilter || undefined,
    search: searchQuery || undefined,
    limit: 100,
    offset: 0,
  });

  const items = useMemo(() => data?.items ?? [], [data]);
  const total = data?.total ?? 0;
  const isReview = mode === 'review';

  async function onDismiss(correspondenceId: string) {
    setActionError(null);
    setDismissing(correspondenceId);
    try {
      await dismiss({ id: correspondenceId }).unwrap();
    } catch (err: unknown) {
      setActionError(
        (err as { data?: { detail?: string } })?.data?.detail ??
          'Не удалось отметить письмо. Попробуйте ещё раз.',
      );
    } finally {
      setDismissing(null);
    }
  }

  return (
    <Panel>
      <Header>
        <div>
          <Title>{isReview ? 'Ждут решения' : 'Отправленные письма'}</Title>
          <Subtitle>
            {isReview
              ? `ОСНОВА не выбрала, кому переслать, или письмо не ушло: ${total}`
              : `Перенаправлено на ящики отделов: ${total}`}
            {isLoading ? ' · загрузка…' : ''}
          </Subtitle>
          {isReview && (
            <Hint>
              Откройте письмо и перешлите его нужным людям. Если пересылать не нужно
              (ответственный уже в копии, письмо разобрано в почте), нажмите
              «Не пересылать».
            </Hint>
          )}
        </div>
      </Header>

      <Controls>
        <SearchInput
          type="search"
          placeholder="Поиск: тема, отправитель или отдел…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        <ChipRow>
          {FILTERS[mode].map(([value, label]) => (
            <Chip
              key={value || 'all'}
              type="button"
              $active={statusFilter === value}
              onClick={() => setStatusFilter(value)}
            >
              {label}
            </Chip>
          ))}
        </ChipRow>
      </Controls>

      {actionError && <ActionError role="alert">{actionError}</ActionError>}

      {isLoading ? (
        <List>
          {Array.from({ length: 5 }).map((_, i) => (
            <SkeletonRow key={i}>
              <Skeleton $width="60%" $height={16} />
              <Skeleton $width="40%" $height={14} />
            </SkeletonRow>
          ))}
        </List>
      ) : isError ? (
        <Empty>
          <EmptyTitle>
            {isReview
              ? 'Не удалось загрузить письма, которые ждут решения'
              : 'Не удалось загрузить отправленные письма'}
          </EmptyTitle>
          <RetryButton type="button" onClick={() => refetch()}>
            Повторить
          </RetryButton>
        </Empty>
      ) : items.length === 0 ? (
        <Empty>
          <EmptyTitle>
            {isReview ? 'Все письма разобраны' : 'Пока нет отправленных писем'}
          </EmptyTitle>
          <EmptyHint>
            {isReview
              ? 'Здесь появятся письма, для которых ОСНОВА не нашла адресата.'
              : 'Письма появятся здесь после того, как агент или юрист перенаправит их на ящик отдела.'}
          </EmptyHint>
        </Empty>
      ) : (
        <List>
          {items.map(({ correspondence, forward, route }) => {
            // Неизвестный статус даёт undefined, и бейдж просто не рисуется.
            const meta = STATUS_META[forward.status] ?? null;
            const reason =
              isReview || forward.status === 'not_needed' ? reviewReason(forward) : null;
            const canDismiss = isReview && can_write;
            return (
              <RowShell key={forward.id}>
                <Row
                  type="button"
                  $withAction={canDismiss}
                  onClick={() => onOpen(correspondence.id)}
                >
                  <RowMain>
                    <RowSubject>{correspondence.subject || '(без темы)'}</RowSubject>
                    <RowSender>{correspondence.sender || 'Отправитель не указан'}</RowSender>

                    {reason ? (
                      <Reason>{reason}</Reason>
                    ) : (
                      /* Ключевой элемент: КУДА отправлено письмо. */
                      <Destination $known={route !== null}>
                        <Arrow aria-hidden="true">→</Arrow>
                        {route ? (
                          <>
                            <DestName>{route.display_name}</DestName>
                            <DestEmail>&lt;{route.email}&gt;</DestEmail>
                          </>
                        ) : (
                          <DestName>—</DestName>
                        )}
                      </Destination>
                    )}
                  </RowMain>

                  <RowMeta>
                    {meta ? <ToneBadge $tone={meta.tone}>{meta.label}</ToneBadge> : null}
                    <MetaDate>
                      {isReview
                        ? formatDt(correspondence.incoming_at)
                        : formatDt(forward.sent_at ?? forward.updated_at)}
                      {!isReview && forward.confidence != null && forward.status !== 'not_needed'
                        ? ` · AI ${Math.round(forward.confidence * 100)}%`
                        : ''}
                    </MetaDate>
                  </RowMeta>
                </Row>
                {canDismiss && (
                  <DismissButton
                    type="button"
                    disabled={dismissing === correspondence.id}
                    onClick={() => onDismiss(correspondence.id)}
                  >
                    {dismissing === correspondence.id ? 'Отмечаю…' : 'Не пересылать'}
                  </DismissButton>
                )}
              </RowShell>
            );
          })}
        </List>
      )}
    </Panel>
  );
}

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(4)};
  max-width: 920px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const Title = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Subtitle = styled.div`
  margin-top: ${({ theme }) => theme.spacing(1)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Controls = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const SearchInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(3)};
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  transition: border-color ${({ theme }) => theme.motion.fast}
    ${({ theme }) => theme.motion.easeOut};

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.muted};
  }

  &:focus-visible {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const Chip = styled.button<{ $active: boolean }>`
  padding: ${({ theme }) => theme.spacing(1)} ${({ theme }) => theme.spacing(3)};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border-radius: ${({ theme }) => theme.radius.pill};
  border: 1px solid
    ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.border)};
  color: ${({ $active, theme }) =>
    $active ? theme.colors.primary : theme.colors.text.secondary};
  background: ${({ $active, theme }) =>
    $active ? theme.colors.primarySoft : theme.colors.surface};
  transition: border-color ${({ theme }) => theme.motion.fast}
      ${({ theme }) => theme.motion.easeOut},
    color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
    border-color: ${({ theme }) => theme.colors.borderStrong};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const rowIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const RowShell = styled.div`
  position: relative;
`;

const Row = styled.button<{ $withAction?: boolean }>`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(4)};
  width: 100%;
  text-align: left;
  padding: ${({ theme }) => theme.spacing(3)} ${({ theme }) => theme.spacing(4)};
  padding-bottom: ${({ theme, $withAction }) =>
    $withAction ? theme.spacing(12) : theme.spacing(3)};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  cursor: pointer;
  animation: ${rowIn} ${({ theme }) => theme.motion.base}
    ${({ theme }) => theme.motion.easeOut};
  transition: border-color ${({ theme }) => theme.motion.fast}
      ${({ theme }) => theme.motion.easeOut},
    background ${({ theme }) => theme.motion.fast}
      ${({ theme }) => theme.motion.easeOut};

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
    border-color: ${({ theme }) => theme.colors.borderStrong};
  }

  &:focus-visible {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    transition: none;
  }
`;

const RowMain = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
`;

const RowSubject = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RowSender = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// Акцентный блок «куда отправлено». Зелёный фон/кант, когда маршрут известен;
// приглушённый — когда route === null.
const Destination = styled.div<{ $known: boolean }>`
  display: inline-flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(1)};
  margin-top: ${({ theme }) => theme.spacing(1)};
  padding: ${({ theme }) => theme.spacing(1)} ${({ theme }) => theme.spacing(2)};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ $known, theme }) =>
    $known ? `${theme.colors.status.success}1A` : theme.colors.surfaceHover};
  border: 1px solid
    ${({ $known, theme }) =>
      $known ? `${theme.colors.status.success}55` : theme.colors.border};
`;

const Arrow = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.status.success};
`;

const DestName = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const DestEmail = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
  word-break: break-all;
`;

// Почему письмо ждёт решения (или почему пересылка не нужна).
const Reason = styled.div`
  margin-top: ${({ theme }) => theme.spacing(1)};
  font-size: 13px;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.text.secondary};
  white-space: normal;
`;

// Кнопка «Не пересылать» лежит поверх нижнего края строки: вложить кнопку в
// кнопку-строку нельзя (HTML), поэтому она — соседний элемент.
const DismissButton = styled.button`
  position: absolute;
  right: ${({ theme }) => theme.spacing(4)};
  bottom: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(1)} ${({ theme }) => theme.spacing(3)};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text.secondary};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  transition: border-color ${({ theme }) => theme.motion.fast}
      ${({ theme }) => theme.motion.easeOut},
    color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:hover:not(:disabled) {
    color: ${({ theme }) => theme.colors.text.primary};
    border-color: ${({ theme }) => theme.colors.borderStrong};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
`;

const Hint = styled.div`
  margin-top: ${({ theme }) => theme.spacing(2)};
  max-width: 640px;
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const ActionError = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.status.danger};
`;

const RowMeta = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${({ theme }) => theme.spacing(1)};
`;

const MetaDate = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  white-space: nowrap;
`;

const SkeletonRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(3)} ${({ theme }) => theme.spacing(4)};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
`;

const Empty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(10)} ${({ theme }) => theme.spacing(4)};
  text-align: center;
`;

const EmptyTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const EmptyHint = styled.div`
  max-width: 420px;
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const RetryButton = styled.button`
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(4)};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.primarySoft};
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.radius.md};
  transition: background ${({ theme }) => theme.motion.fast}
    ${({ theme }) => theme.motion.easeOut};

  &:hover {
    background: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.backgroundElevated};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;
